import type { PromotionOption } from './types';

export function useNextLevelAmount(
  cartPromotions: Ref<{
    options: (Partial<PromotionOption> | null | undefined)[] | null | undefined;
    eligiblePromotionIdx: number;
  }>,
  tierIdx?: ComputedRef<number | null>,
) {
  const nextLevelAmount = computed(() => {
    if (!cartPromotions) {
      return 0;
    }
    // If a tier is specified, use that tier
    // If not, check the tier the user is eligible for
    const eligibleTierIdx = tierIdx?.value ?? cartPromotions.value.eligiblePromotionIdx;

    // If there are no tiers, return 0
    if (!cartPromotions.value.options || !cartPromotions.value.options?.length) {
      return 0;
    }

    // If there are no eligible tiers or the first tier is eligible, return the first tier minimum subtotal
    if (eligibleTierIdx === 0) {
      return cartPromotions.value.options?.[0]?.is_eligible
        ? cartPromotions.value.options?.[1]?.tier_minimum_subtotal || 0
        : cartPromotions.value.options?.[0]?.tier_minimum_subtotal || 0;
    }

    // If the last tier is eligible, return the last tier minimum subtotal
    if (eligibleTierIdx === cartPromotions.value.options.length - 1) {
      return cartPromotions.value.options?.[eligibleTierIdx]?.tier_minimum_subtotal || 0;
    }

    // If the eligible tier is in the middle, return the next tier minimum subtotal
    return cartPromotions.value.options?.[eligibleTierIdx + 1]?.tier_minimum_subtotal || 0;
  });

  return {
    nextLevelAmount,
  };
}

export function useGiftTiers(
  nextLevelAmount: Ref<number>,
  subtotal: Ref<number>,
  cartPriceRuleDiscounts: ComputedRef<{
    tiers: {
      discountValue: number;
    }[];
    eligibleTierIdx: number;
  }>,
) {
  const amountRemaining = computed(() => {
    if (!nextLevelAmount.value) {
      return 0;
    }

    return Math.max(Math.round(nextLevelAmount.value - subtotal.value), 0);
  });

  const progressDone = computed(() => {
    if (!nextLevelAmount.value && cartPriceRuleDiscounts.value.tiers.length > 1) {
      return 0;
    }

    return Math.min((subtotal.value / nextLevelAmount.value) * 100, 100);
  });

  const currentTier = computed(() => {
    return progressDone ? cartPriceRuleDiscounts.value.eligibleTierIdx + 1 : 0;
  });

  const nextTierDiscount = computed(() => {
    return cartPriceRuleDiscounts.value.tiers?.[currentTier.value]?.discountValue;
  });

  return {
    amountRemaining,
    progressDone,
    currentTier,
    nextTierDiscount,
  };
}
